/**
 * Relationship Compass — Coaching Page (Compass)
 *
 * Dedicated /coach route for relationship navigation coaching. The coach
 * needs the person's DDD account (services/CoachingService.js), so a
 * signed-out visitor is asked to log in first. Styled to the app's dark
 * theme, with room for the fixed 80 px header (pt-32, as on the other pages).
 *
 * Australian English throughout.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Compass as CompassIcon, Settings as SettingsIcon, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import coachingService from '../services/CoachingService';
import { useAuth } from '../context/AuthContext';
import {
  CrisisResourcesCard,
  ProfessionalSupportCard,
  SubtleProfessionalHint,
  CoachingDisclaimer,
  UsageIndicator,
  ConsentGate
} from '../components/coaching/CoachingPanels';
import { renderMarkdown } from '../components/coaching/renderMarkdown.jsx';
import { loadModuleContext, clearModuleContext } from '../services/coachModuleContext';

const suggestedPrompts = [
  "I'm not sure if this relationship is right for me. Can we explore it?",
  "Help me understand why I keep choosing the same type of partner.",
  "We're at a crossroads. How do I figure out what I actually want?",
  "I keep pulling away when things get close. Can we unpack that?"
];

const PAGE = 'min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900';

export default function CoachingPage() {
  const { user, redirectToAuth } = useAuth();
  const signedIn = !!user && !user.isAnonymous;
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [crisis, setCrisis] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  // The module the person came from ("Revise with Compass", or the menu while
  // reading one). Kept in sessionStorage so it survives the sign-in redirect.
  const [fromModule, setFromModule] = useState(() => loadModuleContext());
  const dropModuleContext = () => {
    clearModuleContext();
    setFromModule(null);
  };
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!signedIn) {
      setLoadingProfile(false);
      return undefined;
    }
    setLoadingProfile(true);
    (async () => {
      const res = await coachingService.getProfile();
      if (res?.success) setProfile(res.profile);
      else if (res?.response) setError(res.response);
      setLoadingProfile(false);
    })();
    return () => { coachingService.endSession().catch(() => {}); };
  }, [signedIn]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, crisis, professional]);

  const handleGrantConsent = async (opts) => {
    const res = await coachingService.grantConsent(opts);
    if (res?.success) setProfile(res.profile);
    return res;
  };

  const ensureSession = async () => {
    if (sessionStarted) return;
    await coachingService.startSession({ sessionType: 'exploration' });
    setSessionStarted(true);
  };

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    await ensureSession();
    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setInput('');

    const res = await coachingService.askCoach(trimmed, {
      returnUrl: typeof window !== 'undefined' ? window.location.pathname : null,
      module: fromModule
    });

    if (res?.success) {
      setMessages(prev => [...prev, { role: 'assistant', content: res.response, character: res.character }]);
      setCrisis(res.crisisResources || null);
      setProfessional(res.professionalSupport || null);
      setUsage(res.usage || null);
    } else {
      setError(res?.response || 'Something went wrong.');
      if (res?.dailyCapExceeded) {
        setUsage({ tier: res.tier, dailyLimit: res.dailyLimit, used: res.dailyLimit, remaining: 0, upgradeUrl: res.upgradeUrl });
      }
      setMessages(prev => [...prev, { role: 'assistant', content: res?.response || '...', isError: true }]);
    }
    setSending(false);
  };

  if (!signedIn) {
    return (
      <div className={`${PAGE} pt-32 pb-16 px-4 sm:px-6`}>
        <div className="max-w-xl mx-auto bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 sm:p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
            <CompassIcon className="text-white" size={24} aria-hidden="true" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Coaching with Compass</h1>
          <p className="text-slate-300 leading-relaxed mb-6">
            Compass remembers your goals and what you've talked about from one session to the next,
            so it needs your Dive Deep Dialogues account. Log in, or create an account, to start.
          </p>
          <button
            type="button"
            onClick={() => redirectToAuth('signin')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-full font-medium transition-colors"
          >
            <LogIn size={18} aria-hidden="true" /> Log in to start coaching
          </button>
        </div>
      </div>
    );
  }

  if (loadingProfile) {
    return <div className={`${PAGE} pt-32 flex items-start justify-center`}><p className="text-slate-400 mt-16">Loading…</p></div>;
  }

  if (!profile?.consent?.granted) {
    return (
      <div className={`${PAGE} pt-32 pb-16 px-4 sm:px-6`}>
        {error && <p className="max-w-2xl mx-auto mb-4 text-sm text-red-300" role="alert">{error}</p>}
        <ConsentGate onAccept={handleGrantConsent} />
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  return (
    <div className={`${PAGE} pt-28 pb-28`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
              <CompassIcon className="text-white" size={20} aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-white">Coaching with Compass</h1>
              <p className="text-xs sm:text-sm text-slate-400">Not therapy. Not advice. Just a coach who sees the whole picture.</p>
            </div>
          </div>
          <Link to="/coach/privacy" className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-rose-400" title="Coaching privacy" aria-label="Your coaching data">
            <SettingsIcon size={18} aria-hidden="true" />
          </Link>
        </header>

        <div className="mb-3 space-y-2">
          <CoachingDisclaimer />
          <UsageIndicator usage={usage} />
          {fromModule && (
            <p className="text-xs sm:text-sm text-slate-300 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                Compass knows you came from <span className="text-white">{fromModule.title}</span>
                {fromModule.section ? <>, on &ldquo;{fromModule.section}&rdquo;</> : null}.
              </span>
              <button
                type="button"
                onClick={dropModuleContext}
                className="text-rose-300 hover:text-white underline underline-offset-2"
              >
                Talk about something else
              </button>
            </p>
          )}
        </div>

        {!hasMessages && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-5 mb-4">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
              Hi {profile?.personalContext?.firstName || 'there'}, where are things at for you? We can
              explore a decision you're facing, dig into a pattern you keep noticing, or figure out
              what you actually want.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {suggestedPrompts.map((p) => (
                <button key={p} onClick={() => send(p)}
                  className="text-left text-sm sm:text-base text-slate-200 bg-slate-900/40 hover:bg-rose-500/10 hover:text-rose-200 border border-slate-700 hover:border-rose-500/40 rounded-lg p-3 transition">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <AnimatePresence>
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm sm:text-base leading-relaxed ${
                  m.role === 'user' ? 'bg-rose-600 text-white'
                    : m.isError ? 'bg-red-900/30 text-red-200 border border-red-500/30'
                    : 'bg-slate-800/70 text-slate-100 border border-slate-700/50'
                }`}>
                  {m.role === 'assistant' && !m.isError ? renderMarkdown(m.content) : m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {crisis && <CrisisResourcesCard resources={crisis} />}
          {!crisis && professional?.tone === 'amber' && <ProfessionalSupportCard support={professional} />}
          {!crisis && professional?.tone === 'subtle' && <SubtleProfessionalHint support={professional} />}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-800/70 border border-slate-700/50 rounded-2xl px-4 py-3 text-slate-400 text-sm">Compass is thinking…</div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700 px-4 sm:px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            rows={1} placeholder="Say what's on your mind…" aria-label="Your message to Compass"
            className="flex-1 resize-none rounded-xl border border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 max-h-40"
            disabled={sending} maxLength={2000} />
          <button type="submit" aria-label="Send message" disabled={sending || !input.trim()}
            className="flex-shrink-0 p-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 disabled:opacity-50">
            <Send size={18} />
          </button>
        </div>
        {error && <p className="max-w-3xl mx-auto text-xs sm:text-sm text-red-300 mt-1">{error}</p>}
      </form>
    </div>
  );
}
